/**
 * Adding a person in Keystone, and editing one (#731): a contact with no CE21 account.
 *
 *   POST  /api/desk/person          { first_name, last_name, email, phone?, allow_namesakes? } → 201 { id }
 *   PATCH /api/desk/person/<id>     any of { first_name, last_name, email, phone }            → 200 { id, changed }
 *   DELETE /api/desk/person/<id>    a person added by mistake                                  → 204
 *
 * Staff and the manager only. A 409 says who: `conflict: "email"` when someone has the address (never a second
 * person), `"name"` when someone has the name (send `allow_namesakes` to add them anyway), `"ce21"` when the person
 * is CE21's (CE21 owns their details), `"history"` when a removal finds notes or history (they stay, #715's rule). A 400 carries DRF's field errors.
 */
import { apiRequest, type ApiError } from "./client";

export interface PersonInput {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

export type PersonField = keyof PersonInput;

/** Someone a 409 names: who has the email, or the name. */
export interface ConflictPerson {
  id: number;
  name: string;
  email: string | null;
}

export interface PersonConflict {
  detail: string;
  conflict: "email" | "name" | "ce21" | "history";
  people: ConflictPerson[];
}

export function addPerson(input: PersonInput & { allow_namesakes?: boolean }): Promise<{ id: number }> {
  return apiRequest("/desk/person", { method: "POST", body: JSON.stringify(input) });
}

export function editPerson(id: number, changes: Partial<PersonInput>): Promise<{ id: number; changed: PersonField[] }> {
  return apiRequest(`/desk/person/${id}`, { method: "PATCH", body: JSON.stringify(changes) });
}

export function removePerson(id: number): Promise<void> {
  return apiRequest(`/desk/person/${id}`, { method: "DELETE" });
}

/** The conflict a 409 carries, or null for any other error. */
export function conflictOf(error: unknown): PersonConflict | null {
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  const found = body as Partial<PersonConflict> | undefined;
  return status === 409 && found?.conflict ? { detail: found.detail ?? "", conflict: found.conflict, people: found.people ?? [] } : null;
}

/** The first error for each field a 400 names, or null for any other error. */
export function fieldErrorsOf(error: unknown): Partial<Record<PersonField, string>> | null {
  const { status, body } = (error ?? {}) as Partial<ApiError>;
  if (status !== 400 || !body || typeof body !== "object") return null;
  const out: Partial<Record<PersonField, string>> = {};
  for (const key of ["first_name", "last_name", "email", "phone"] as const) {
    const messages = (body as Record<string, unknown>)[key];
    if (Array.isArray(messages) && typeof messages[0] === "string") out[key] = messages[0];
  }
  return out;
}
